import type React from "react";
import { Box, Button } from "@mui/material";
import { Facebook, WhatsApp, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IStreamDetails } from "../../../types/streams";
import { RootState } from "../../../store/Index";

interface StreamHeaderProps {
  stream: IStreamDetails;
  onCreateThumbnail: () => void;
  onOpenChannelSelectModal: () => void;
  onCreateYouTubeLink: () => void;
  onSocialShare: (platform: "facebook" | "whatsapp") => void;
}

const StreamHeader: React.FC<StreamHeaderProps> = ({
  stream,
  onCreateThumbnail,
  onCreateYouTubeLink,
  onSocialShare,
  onOpenChannelSelectModal,
}) => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const handleNavigateToMemorial = () => {
    const id = stream.booking.id;
    const serviceType = stream.booking?.service;
    let slugSource = "";
    let pageType = "";

    if (serviceType === "Funeral" || serviceType === "FuneralHome") {
      const firstName = stream.booking?.deceased?.[0]?.first_name || "";
      const surname = stream.booking?.deceased?.[0]?.surname || "";
      slugSource = `${firstName} ${surname}`.trim();
      pageType = "memorial-page";
    } else if (serviceType === "Church") {
      slugSource = stream.booking?.events?.[0]?.title || "church-service";
      pageType = "church-page";
    } else if (serviceType === "Conference") {
      slugSource = stream.booking?.events?.[0]?.title || "conference";
      pageType = "conference-page";
    } else {
      slugSource = "service";
      pageType = "service-page";
    }

    const slug = encodeURIComponent(
      slugSource.replace(/\s+/g, "-").toLowerCase()
    );

    navigate(`/${pageType}/${id}/${slug}`);
  };

  const buttonStyle = {
    border: `2px solid ${reduxTheme.primary_colors[100]}`,
    color: reduxTheme.primary_colors[100],
    mr: 1,
  };

  const handleCreateYouTubeLink = () => {
    onOpenChannelSelectModal();
    onCreateYouTubeLink();
  };
  const serviceType = stream.booking?.service;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        mb: 3,
        flexWrap: "wrap",
      }}
    >
      <Button variant="outlined" sx={buttonStyle} onClick={onCreateThumbnail}>
        Create Thumbnail
      </Button>
      <Button
        variant="outlined"
        sx={buttonStyle}
        onClick={handleCreateYouTubeLink}
      >
        Create YouTube Link
      </Button>
      <Button
        variant="outlined"
        sx={buttonStyle}
        onClick={handleNavigateToMemorial}
      >
        {serviceType === "Church" ||
        serviceType === "Other" ||
        serviceType === "Conference"
          ? "Stream Page"
          : "Memorial Page"}
      </Button>
      <Button
        variant="outlined"
        sx={buttonStyle}
        onClick={() =>
          navigate(`/stream-management/view/${stream.id}/callsheet`)
        }
      >
        Callsheet
      </Button>
      {serviceType !== "Church" && (
        <Button
          variant="outlined"
          sx={buttonStyle}
          onClick={() =>
            navigate(`/stream-management/view/${stream.id}/table-tent`)
          }
        >
          Table Tent
        </Button>
      )}

      <Button
        variant="outlined"
        sx={buttonStyle}
        onClick={() =>
          navigate(`/stream-management/view/${stream.id}/stream-report`, {
            state: {
              deceasedName: stream.booking.deceased?.[0]?.first_name || "N/A",
            },
          })
        }
      >
        Stream Report
      </Button>

      <Button
        startIcon={<Facebook />}
        sx={{
          backgroundColor: "#1877F2",
          color: reduxTheme.primary_colors[900],
          mr: 1,
          "&:hover": { backgroundColor: "#0f65d4" },
        }}
        onClick={() => onSocialShare("facebook")}
      >
        Share
      </Button>
      <Button
        startIcon={<WhatsApp />}
        sx={{
          backgroundColor: "#25d366",
          color: reduxTheme.primary_colors[900],
          mr: 1,
          "&:hover": { backgroundColor: "#1da851" },
        }}
        onClick={() => onSocialShare("whatsapp")}
      >
        Share
      </Button>
      <Button
        variant="outlined"
        startIcon={<Edit />}
        sx={{
          backgroundColor: reduxTheme.primary_colors[400],
          color: reduxTheme.primary_colors[900],
        }}
        onClick={() => navigate(`/stream-management/view/${stream.id}/edit`)}
      >
        Edit
      </Button>
    </Box>
  );
};

export default StreamHeader;
